import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Button } from "./Button";

/**
 * Shared pagination controls used across all admin list pages.
 *
 * Props:
 *   page         - current 1-based page number
 *   totalPages   - total number of pages
 *   onPageChange - callback(newPage)
 */
export function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  let start = Math.max(1, page - 2);
  let end = Math.min(totalPages, start + 4);
  if (end - start < 4) start = Math.max(1, end - 4);

  if (start > 1) pages.push(<span key="ds" className="px-1 text-muted-foreground">…</span>);
  for (let i = start; i <= end; i++) {
    pages.push(
      <Button
        key={i}
        variant={i === page ? "default" : "outline"}
        size="icon"
        className="sm:h-8 sm:w-8"
        onClick={() => onPageChange(i)}
      >
        {i}
      </Button>
    );
  }
  if (end < totalPages) pages.push(<span key="de" className="px-1 text-muted-foreground">…</span>);

  return (
    <div className="flex flex-wrap items-center justify-center gap-1 pt-2">
      <Button variant="outline" size="icon" className="sm:h-8 sm:w-8" onClick={() => onPageChange(1)} disabled={page <= 1} title="First page" aria-label="First page">
        <ChevronsLeft className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="icon" className="sm:h-8 sm:w-8" onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page <= 1} title="Previous page" aria-label="Previous page">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      {pages}
      <Button variant="outline" size="icon" className="sm:h-8 sm:w-8" onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages} title="Next page" aria-label="Next page">
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="icon" className="sm:h-8 sm:w-8" onClick={() => onPageChange(totalPages)} disabled={page >= totalPages} title="Last page" aria-label="Last page">
        <ChevronsRight className="h-4 w-4" />
      </Button>
      <div className="ml-0 mt-2 flex w-full items-center justify-center gap-1 sm:ml-2 sm:mt-0 sm:w-auto">
        <span className="text-sm text-muted-foreground">Go to</span>
        <input
          type="number"
          min={1}
          max={totalPages}
          className="w-14 h-10 sm:h-8 rounded-md border border-input bg-background px-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const val = parseInt(e.target.value);
              if (val >= 1 && val <= totalPages) onPageChange(val);
              e.target.value = "";
            }
          }}
        />
      </div>
    </div>
  );
}
